import { useEffect } from "react";
import { message } from "antd";
import { t } from "i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSerializeCallback } from "@kalodata/tools";
import { fetchVideoUrl } from "@apis/http/api";
import {
  PathsApiVideoVideoUrlGetParametersQueryVideoSource,
  VideoRemakeRequestVideoSource,
} from "@apis/generated/openapi";
import type { ReferenceVideoState } from "./studio/types";

/**
 * 解析 Welcome 页的 URL query（id / language / region）：
 * 若存在 id，则调用 /api/video/video-url 获取参考视频 URL，并自动跳转至 Studio。
 *
 * - `videoSource` 固定为 Kalodata
 * - `language` / `region` 仅接收、暂不使用（保留参数）
 * - 跳转逻辑与在库中选择视频并确认时一致（navigate + state）
 */
export function useWelcomeReferenceFromUrl() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("id");

  // useSerializeCallback 折叠并发调用（StrictMode 双调用 / 重复触发只会发起一次请求），
  // 并通过 isPending() 自带 loading 状态。
  const loadReference = useSerializeCallback(async (videoId: string) => {
    try {
      const data = await fetchVideoUrl({
        id: videoId,
        videoSource: PathsApiVideoVideoUrlGetParametersQueryVideoSource.Kalodata,
      });

      const videoUrl = data?.url;
      if (!videoUrl) {
        throw new Error("empty video url");
      }

      const referenceVideo: ReferenceVideoState = {
        kind: "welcome",
        videoUrl,
        videoId,
        videoSource: VideoRemakeRequestVideoSource.Kalodata,
      };
      const language = searchParams.get("language") ?? undefined;
      const region = searchParams.get("region") ?? undefined;
      navigate("/video-remake/studio", { state: { referenceVideo, language, region } });
    } catch {
      message.error(t("Failed to load reference video. Please try again."));
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("id");
          return next;
        },
        { replace: true },
      );
    }
  });

  useEffect(() => {
    if (!id) return;
    void loadReference(id);
  }, [id, loadReference]);

  // 有 id 时即视为加载中（避免首帧闪烁），失败后会清除 id 自动结束；isPending 覆盖收尾阶段。
  return { autoLoading: Boolean(id) || loadReference.isPending() };
}
